import HOOK from "../components/hooks"
import { setNodeAngles } from './setNodeAngles'
import { BASE, CATEGORY, CONSTANTS, KINSHIP, LINKS, RADIO, TAB, TENSE_ENUM } from '../modules/constants'
import { CHECKBOX } from '../modules/checkbox_constants'
import { getChecked } from "../pages/companiesHouse/pageFunctions/checkboxFunctions"

export const nexusDataPrep = {

  getConnectedNodes: props => {
    // console.log('getConnectedNodes props:', props)

    const { enableSecondLevel, entities, features, hubId } = props
    const levelCount = [1, 0, 0]
    const existingNodeIds = [hubId]

    // level 0 - hub node (level 0)
    let level0Entity = entities.items[hubId]
    level0Entity.showPosition = features.EnablePosition

    // Initialise node array with the hub node
    const nodes = [{
      level: 0,
      entity: level0Entity,
      id: level0Entity.entityId,
      filings: getFilingsArray(level0Entity.companyFilingHistory),
    }]
    // level 1 - nodes and their connections from hub
    let level1NodeIds = setNodes(
      {
        ...props,
        connectionIds: level0Entity.connectionIds,
        existingNodeIds,
        level: 1,
        nodes,
        thisNodeId: hubId,
      })

    levelCount[1] = level1NodeIds.length
    // console.log(`level1NodeIds: `, level1NodeIds)

    // Reorder the level 1 nodes (and their node objects already pushed into 'nodes') so that ones
    // sharing a connection - either a common level 2 target, or a direct link to each other - end up
    // adjacent around the hub. setNodeAngles.js assigns each node's angle purely from this order, so
    // placing related nodes next to each other keeps their links short and reduces how often they cross.
    level1NodeIds = orderLevel1ForMinimalCrossings({ enableSecondLevel, entities, hubId, level1NodeIds })
    const level1Nodes = nodes.splice(1, level1NodeIds.length)
    const level1NodeById = new Map(level1Nodes.map(node => [node.id, node]))
    level1NodeIds.forEach(id => nodes.push(level1NodeById.get(id)))

    // level 2 - nodes and their connections from level 1 nodes
    if (enableSecondLevel && CONSTANTS.GET_LEVEL_2) {
      // for each level 1 node...
      level1NodeIds.forEach(level1NodeId => {
        // console.log(`nodeId: `, level1NodeId)
        let level1Entity = entities.items[level1NodeId]
        // console.log(`level1Entity: `, level1Entity)

        let level2NodeIds = setNodes(
          {
            ...props,
            connectionIds: level1Entity.connectionIds,
            existingNodeIds,
            level: 2,
            nodes,
            thisNodeId: level1NodeId,
          })

        if (level2NodeIds.length) levelCount[2] += level2NodeIds.length
        // console.log(`level2NodeIds: `, level2NodeIds)
      })
    }
    // console.log("1. nodes: ", nodeData.nodes)
    return { nodes, levelCount }
  },

  sortNodes: props => {
    const { nodes } = props

    // sort by level
    nodes.sort((a, b) => levelIsGreaterThen(a, b) ? 1 : levelIsGreaterThen(b, a) ? -1 : 0)

    if (CONSTANTS.ENABLE_INITIAL_POSITIONS)
      setNodeAngles(props) // must be done after sorting nodes
  },
}

const levelIsGreaterThen = (m, n) => m.entity.level > n.entity.level

// Returns the ids of every other entity a given entity is directly connected to (excluding itself and the hub)
const getConnectedEntityIds = ({ entities, entityId, hubId }) => {
  const entity = entities.items[entityId]
  const connectedIds = new Set()

  for (const key in entity.connectionIds) {
    const connection = entities.connections[entity.connectionIds[key]]
    const otherId = connection.parentId === entityId ? connection.childId : connection.parentId

    if (otherId !== hubId && otherId !== entityId)
      connectedIds.add(otherId)
  }

  return connectedIds
}

// Scores how strongly two level 1 nodes should sit next to each other: a direct link between them
// (only relevant when not showing 2nd degree connections) counts most, then each level 2 target they
// have in common (relevant when showing 2nd degree connections)
const sharedConnectionScore = (idA, idB, connectedIdsById) => {
  const idsA = connectedIdsById.get(idA)
  const idsB = connectedIdsById.get(idB)
  let score = idsA.has(idB) ? 2 : 0

  idsA.forEach(id => { if (idsB.has(id)) ++score })

  return score
}

// Reorders level 1 node ids using a greedy nearest-neighbour chain: starting from the first node,
// repeatedly extend the chain with whichever remaining node shares the most connections with the node
// just placed. This is a heuristic (minimising crossings exactly is an NP-hard problem) but it reliably
// clusters related nodes - eg officers who share another directorship, or who are directly linked to
// each other - next to each other around the hub, which is what setNodeAngles.js uses to lay them out.
const orderLevel1ForMinimalCrossings = ({ enableSecondLevel, entities, hubId, level1NodeIds }) => {
  if (level1NodeIds.length < 3)
    return level1NodeIds

  const connectedIdsById = new Map(level1NodeIds.map(id =>
    [id, getConnectedEntityIds({ entities, entityId: id, hubId })]))

  // Without 2nd degree connections, only direct links between level 1 nodes are relevant - if there
  // are none of those, the original order is as good as any and there's no need to reorder
  if (!enableSecondLevel && level1NodeIds.every(id => !level1NodeIds.some(otherId =>
    otherId !== id && connectedIdsById.get(id).has(otherId))))
    return level1NodeIds

  const remaining = [...level1NodeIds]
  const ordered = [remaining.shift()]

  while (remaining.length > 0) {
    const lastId = ordered[ordered.length - 1]
    let bestIndex = 0
    let bestScore = -1

    remaining.forEach((id, index) => {
      const score = sharedConnectionScore(lastId, id, connectedIdsById)
      if (score > bestScore) {
        bestScore = score
        bestIndex = index
      }
    })

    ordered.push(remaining.splice(bestIndex, 1)[0])
  }

  return ordered
}

// Returns true if companyProps.lse is null and OTHER_INDEX
// is checked, or if companyProps.lse.indexBit (inc. aic) is checked
const isIndexSelected = ({ checkboxes, entity, radio, tabIndex }) => {
  const { companyProps } = entity

  // Is a corporate PSC
  var corporatePsc =
    tabIndex === TAB[BASE.COMPANY].INDEX // If we are on Company tab ...
    && entity.category === CATEGORY.COMPANY // and the entity is a company,
    && radio[BASE.COMPANY] !== RADIO[BASE.COMPANY].CONNECTIONS // and connections is not selected
    && entity.companyProps.lse === null // and it's not an lse company (we don't know of any lse companies with corp PSCs to test this )

    // Always return true if person or corporate psc,
    // else true or false depending of checkboxes
  return entity.category === CATEGORY.PERSON || corporatePsc
    ? true
    : !!companyProps?.lse?.indexBit
      ? !!(checkboxes[HOOK.LSE_INDEX] & companyProps.lse?.indexBit) &&
      ((getChecked({ checkboxes, hook: HOOK.COMPANY_TYPE })[0] && !companyProps.lse.aic) ||
        (getChecked({ checkboxes, hook: HOOK.COMPANY_TYPE })[1] && companyProps.lse.aic))
      : getChecked({ checkboxes, hook: HOOK.OTHER_INDEX })[0]
}

// For RADIO[BASE.COMPANY].DASHBOARD we will filter roles by current and outgoing tense
const currentAndOutgoingBits = CHECKBOX[HOOK.TENSE][TENSE_ENUM.CURRENT].BIT | CHECKBOX[HOOK.TENSE][TENSE_ENUM.NOTICE].BIT

const setNodes = props => {
  // console.log('setNodes props:', props)
  const { checkboxes, connectionIds, entities, existingNodeIds, level, nodes, radio, tabIndex, thisNodeId } = props
  const nodeIds = []
  const checked = { sex: getChecked({ checkboxes, hook: [HOOK.SEX] }) }

  for (var id in connectionIds) {
    const thisConnection = entities.connections[connectionIds[id]]

    // determine whether the parent or the child is this node
    let connectedEntity = thisConnection.parentId === thisNodeId
      ? entities.items[thisConnection.childId]
      : entities.items[thisConnection.parentId]

    if (isIndexSelected({ checkboxes, entity: connectedEntity, radio, tabIndex })) {
      // check if node for this connected entity already exists
      let connectedNodeExists = existingNodeIds.some(id => id === connectedEntity.entityId)

      // NB: position is passed as ref. and it's props are assigned via anyCapacities
      const position = { tense: null, tenseBits: 0, typeBits: 0 }
      thisConnection.capacities && anyCapacities({ ...props, thisConnection, position, currentAndOutgoingBits })
      connectedEntity.position = position

      // If not an lse company, we generally won't know gender so show regardless of sex
      const sexSelected = isSexSelected(connectedEntity, checked)

      if (!connectedNodeExists  // only create node if it does not already exist,
        && position.show // add node only if it has a role with tense and position selected
        && sexSelected // and sex selected
      ) {
        nodeIds.push(connectedEntity.entityId)
        existingNodeIds.push(connectedEntity.entityId)

        if (level < 3) {
          setNode(
            {
              nodes,
              entity: connectedEntity,
              lConnectId: thisNodeId,
              level,
              positionName: position.name,
              tense: position.tense,
              tenseBits: position.tenseBits,
              kinship: thisConnection.parentId === thisNodeId
                ? KINSHIP.CHILD
                : KINSHIP.PARENT,
            })
        }
      }
    }
  }
  return nodeIds
}

const setNode = ({ nodes, entity, lConnectId, level, kinship, positionName, tense, tenseBits }) => {
  nodes.push({
    level,
    entity,
    id: entity.entityId,
    positionName,
    tense,
    tenseBits,
    filings: level === 0 ? getFilingsArray(entity.companyFilingHistory) : [],
    lConnectId,
    kinship,
  })
}

// Return true if sex is selected or it's a company
const isSexSelected = (entity, checked) => entity.category === CATEGORY.COMPANY
  ? true // Return true it's a company, so sex is irrelevant
  : entity.personInfo.sex === null
    ? checked.sex[2] // Return: is 'sex unknown' checked?
    : checked.sex[entity.personInfo.sex] // Return: is the person's sex checked?

const getFilingsArray = filingHistory => filingHistory
  ? filingHistory.items.map(item => ({
    // documentMetaDataLink: item.links.documentMetaData,
    pdfLink: item.resources.applicationPdf
      ? `${LINKS.COMPANIES_HOUSE}${item.links.self}/document?format=pdf`
      : null,
    xhtmlLink: item.resources.applicationXhtml
      ? `${LINKS.COMPANIES_HOUSE}${item.links.self}/document?format=xhtml`
      : null,
    // filingDescription: item.description,
    // descriptionToPresent: item.descriptionToPresent,
  }))
  : []

const anyCapacities = props => {
  const { thisConnection, position, currentAndOutgoingBits } = props

  const id = thisConnection.childId
  position.show = false

  for (var capacity in thisConnection.capacities) {
    anyRoles({ ...props, roles: thisConnection.capacities[capacity], id, currentAndOutgoingBits })
  }
  return
}

const anyRoles = props => {

  // console.log('anyRoles props:', props)
  const { checkboxes, features, position, roles, id, radio, currentAndOutgoingBits } = props

  const lseCompany = !!props.hubEntity?.companyProps?.lse

  for (let i = 0; i < roles.length; ++i) {
    const role = roles[i]

    // tenseBits is an accumulation of the different roles an officer may have
    position.tenseBits = position.tenseBits | role.tenseBit

    role.typeBit = CHECKBOX[HOOK.POSITION][role.position.type].BIT // TODO defined typeBit in backend
    // typeBits is an accumulation of the different role types an officer may have
    position.typeBits = position.typeBits | role.typeBit

    // If position.tense is undefined or is 'later' than this role
    // and the role is displayed
    if ((position.tense == null || (role.tense < position.tense))
      && !!(checkboxes[HOOK.TENSE] & role.tenseBit)
      // && !!BITWISE.and(checkboxes, CHECKBOX[HOOK.POSITION][role.position.type].BIT)
    ) {
      position.tense = role.tense
      position.compensationGBP = role.compensationGBP

      if (role.position) {
        position.type = role.position.type // We need the type so that we can filter eg secretaries..
        if (features.EnablePosition)
          position.name = role.position.title // but don't show the title (eg 'Chairman') if not features.EnablePosition
      }
    }

    // NEW
    role.show =
      (lseCompany && (radio[BASE.COMPANY] === RADIO[BASE.COMPANY].DASHBOARD))
        ? !!(currentAndOutgoingBits & role.tenseBit)
        : !!(checkboxes[HOOK.TENSE] & role.tenseBit)
        && !!(checkboxes[HOOK.POSITION] & role.typeBit)
        && !!(checkboxes[HOOK.EXEC] & getBit(role.position.exec))
        && !!(checkboxes[HOOK.INDP] & getBit(role.position.independent))

    if (role.show) position.show = true
  }

  return
}

// Returns 1 if bool is true, 2 if false, or 4 if null
const getBit = bool => bool == true ? 1 : bool === false ? 2 : 4

